/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { Fragment, useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useHistory, useParams } from 'react-router-dom';

import { isContainedInProcessApplication } from 'utils/helpers';
import { milestoneStore, diagramControlStore, diffingStore, notificationStore, processApplicationStore } from 'stores';
import hasAccess, { actions } from 'utils/user-access';
import { projectService, fileService, trackingService, folderService, tracingService } from 'services';
import { CodeEditor } from 'components';
import { Spinner } from 'primitives';

import Sidebar from './Sidebar';
import Header from './Header';
import * as Styled from './FormMilestones.styled';

const FormMilestones = () => {
  const history = useHistory();
  const params = useParams();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackingService.trackPageView('milestone');

    init();

    return () => {
      milestoneStore.reset();
      diffingStore.reset();
      diagramControlStore.reset();
    };
    // @ts-expect-error TS2339
  }, [params.id]);

  const init = async () => {
    setLoading(true);

    try {
      // @ts-expect-error TS2339
      const file = await fileService.fetchById(params.id);

      const requests = [projectService.fetchById({ projectId: file.projectId })];
      if (isContainedInProcessApplication(file)) {
        const skipParentInitialization = true;
        requests.push(processApplicationStore.init(file.folderId, skipParentInitialization));
      } else if (file.folderId) {
        requests.push(folderService.fetchById(file.folderId));
      }
      const [project, folder] = await Promise.all(requests);
      if (!hasAccess(project, actions.VIEW_VERSIONS)) {
        // @ts-expect-error TS2339
        return history.push(`/forms/${params.id}`);
      }
      // @ts-expect-error TS2339
      const milestoneIdsFromUrl = params.slug?.split('...');

      // @ts-expect-error TS2554
      milestoneStore.init({ ...file, folder }, project, [], milestoneIdsFromUrl);
      await milestoneStore.fetchAll();

      milestoneStore.trackMilestoneView('milestone-history');

      setLoading(false);
    } catch (ex) {
      notificationStore.showError('Yikes! Could not load the version data. Please try again later.');
      tracingService.traceError(ex, 'Failed to load version data');
    }
  };

  return (
    <Styled.Container>
      {loading ? (
        <Spinner fullHeight />
      ) : (
        <Fragment>
          <Header />
          <Styled.LeftSection>
            {/* @ts-expect-error TS2741 */}
            <CodeEditor
              titles={milestoneStore.titles}
              value={milestoneStore.primaryContent}
              modified={milestoneStore.secondaryContent}
            />
          </Styled.LeftSection>
          <Sidebar />
        </Fragment>
      )}
    </Styled.Container>
  );
};

export default observer(FormMilestones);
